import type { SeriesRecord } from '@/components/Chart/types/chartData';
import type { ExtendedSeriesOptionsType, SeriesContext } from '@/components/Chart/types/highcharts';
import { formatPriceValue } from '@/utils/formatPriceValue';

export type TimeSeriesPointCustom = {
  timeRef?: string;
  timezone?: string;
  transactionDate?: number;
  periodAbs?: string;
  decimalPlaces: number;
};
export type TimeSeriesPoint = {
  x: number | undefined;
  y: number;
  custom?: TimeSeriesPointCustom;
};

export type BuildChartSeriesOptions = {
  name: string;
  id: string;
  color?: string;
  timeSeriesPoints: TimeSeriesPoint[] | SeriesRecord[];
  context: SeriesContext;
  visible?: boolean;
  isDaily?: boolean;
};

const LAST_PRICE_LABEL_STYLE = {
  color: '#000000',
  fontSize: '10px',
  fontStyle: 'normal',
  fontWeight: '400',
  lineHeight: 'normal',
  textAlign: 'center',
  textOutline: 'none'
} as const;
const LAST_PRICE_LABEL_HORIZONTAL_PAD = '\u00A0\u00A0';

export const buildChartRangeSeries = ({
  name,
  id,
  timeSeriesPoints,
  context
}: BuildChartSeriesOptions): ExtendedSeriesOptionsType => ({
  data: timeSeriesPoints,
  type: 'arearange',
  lineWidth: 0,
  zIndex: -1,
  linkedTo: ':previous',
  name,
  id,
  custom: {
    ...context
  }
});

export const buildChartLineSeries = ({
  name,
  id,
  timeSeriesPoints,
  context,
  visible = true,
  isDaily = false
}: BuildChartSeriesOptions): ExtendedSeriesOptionsType => ({
  type: 'line',
  name,
  data: timeSeriesPoints,
  id,
  visible,
  connectNulls: !isDaily,
  dataGrouping: {
    approximation: 'close',
    lastAnchor: 'lastPoint'
  },
  marker: {
    radius: 1,
    symbol: 'circle'
  },
  gapSize: 10,
  lastPrice: {
    enabled: false
  },
  lastVisiblePrice: {
    color: 'transparent',
    width: 0,
    label: {
      borderRadius: 0,
      borderWidth: 0,
      padding: 0,
      shape: 'rect',
      style: LAST_PRICE_LABEL_STYLE,
      formatter(this, value: number) {
        const labelValue = formatPriceValue(context.decimalPlaces, value);
        return `${LAST_PRICE_LABEL_HORIZONTAL_PAD}${labelValue}${LAST_PRICE_LABEL_HORIZONTAL_PAD}`;
      }
    }
  },
  color: context.color,
  showInNavigator: true,
  custom: {
    hideName: Boolean(name),
    ...context
  }
});
